import { motion, useScroll, useTransform } from "framer-motion";
import { Crosshair, ArrowDown, MessageCircle } from "lucide-react";
import TopoCanvas from "./TopoCanvas";
import { scrollToSection } from "./LenisProvider";
import { WHATSAPP_URL } from "./constants";

const HERO_IMG =
  "https://images.unsplash.com/photo-1517256985756-924657c74fba?crop=entropy&cs=srgb&fm=jpg&w=2400&q=80";

const lineVariants = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 0.95, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HUD_ROWS = [
  ["REFERÊNCIA", "SIRGAS 2000"],
  ["FUSO UTM", "24S"],
  ["LOCALIZAÇÃO", "FORTALEZA — CE"],
];

export default function Hero({ start = true }) {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 150]);
  const bgScale = useTransform(scrollY, [0, 700], [1.08, 1]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.15]);

  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Vista aérea de terreno mapeado"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#050b14]/72" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/70" />
      </motion.div>

      <TopoCanvas className="absolute inset-0 w-full h-full opacity-80 pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 w-full pt-28 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="flex items-center gap-3 font-mono text-[11px] sm:text-xs uppercase tracking-[0.32em] text-dm-contour"
        >
          <Crosshair size={14} className="pulse-soft" />
          DM Levantamentos Topográficos — Fortaleza, CE
        </motion.p>

        <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.04] text-[clamp(2.6rem,7.2vw,5.9rem)]">
          {["Precisão que", "define o seu", "projeto."].map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-gradient" : "text-white"}`}
                variants={lineVariants}
                custom={i}
                initial="hidden"
                animate={start ? "visible" : "hidden"}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
        >
          Soluções topográficas avançadas para construção civil, agronegócio e
          regularização fundiária. Tecnologia de ponta a serviço da sua obra.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-orcamento"
            onClick={() => scrollToSection("contato")}
            className="group rounded-full bg-dm-blue hover:bg-[#0052d9] text-white font-semibold px-8 py-4 shadow-[0_0_28px_rgba(0,98,255,0.5)] transition-all hover:shadow-[0_0_44px_rgba(0,98,255,0.75)] hover:-translate-y-0.5"
          >
            Solicitar Orçamento
          </button>
          <a
            data-testid="hero-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-white/15 glass text-white font-semibold px-8 py-4 flex items-center justify-center gap-2.5 hover:border-dm-glow/60 hover:bg-white/10 transition-all"
          >
            <MessageCircle size={18} className="text-dm-glow" />
            Falar com Especialista
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={start ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
        transition={{ delay: 1.5, duration: 0.9 }}
        className="absolute right-8 bottom-28 hidden xl:block z-10"
      >
        <div className="glass rounded-2xl px-6 py-5 w-[290px] shadow-[0_8px_32px_rgba(0,10,30,0.6)]">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-dm-contour mb-4">
            Telemetria de Campo
          </p>
          <div className="space-y-3">
            {HUD_ROWS.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-4">
                <span className="font-mono text-[10px] tracking-widest text-slate-500">{k}</span>
                <span className="font-mono text-xs text-slate-100">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-soft" />
            <span className="font-mono text-[10px] tracking-widest text-slate-400">
              SINAL GNSS ESTÁVEL
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-slate-400"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">explorar</span>
        <span className="h-8 w-[1px] bg-slate-500/60 overflow-hidden">
          <span className="scroll-cue-dot block h-3 w-[1px] bg-dm-glow" />
        </span>
        <ArrowDown size={12} />
      </motion.div>
    </section>
  );
}
